import { defineStore } from 'pinia'
import uuid from 'uuid-random'
import lscache from 'lscache'

// lscache key + TTL. lscache expiry is expressed in MINUTES (mirrors med-web-funnel's `ONE_DAY`).
const STORAGE_KEY = 'med-account-session'
const ONE_DAY_MINUTES = 24 * 60

// Captured at module load on purpose: the `/` route is a dispatcher whose guard
// (src/auth/routeGuard.ts) redirects to /signin or /status and drops the query string before any
// component renders. This is the only moment the arrival params (?from, ?cancellation_session_id)
// are still on the URL. See docs/decisions/0008-session-id.md.
const arrivalQuery = new URLSearchParams(window.location.search)

// Same capture-at-module-load rationale as arrivalQuery, for the path: main.ts gates
// CancellationSessionStart on the pre-redirect arrival pathname.
const arrivalPath = window.location.pathname

interface StoredSession {
	sessionId: string
	queryParams: Record<string, string>
}

interface LocationState {
	sessionId: string
	queryParams: Record<string, string>
}

function snapshot(params: URLSearchParams): Record<string, string> {
	return Object.fromEntries(params.entries())
}

// Returns the persisted session, or null when missing/expired (lscache evicts expired entries on get).
function readStored(): StoredSession | null {
	const blob = lscache.get(STORAGE_KEY) as StoredSession | null
	if (!blob || typeof blob.sessionId !== 'string' || blob.sessionId === '') return null
	return { sessionId: blob.sessionId, queryParams: blob.queryParams ?? {} }
}

function writeStored(blob: StoredSession): void {
	lscache.set(STORAGE_KEY, blob, ONE_DAY_MINUTES)
}

export const useLocationStore = defineStore('location', {
	state: (): LocationState => ({
		sessionId: '',
		queryParams: {},
	}),

	getters: {
		// Persisted arrival source — survives the 24h session (reads the stored snapshot), so every
		// analytics event can carry `source` even after the Auth0 round-trip / on later screens.
		from: (state): string | null => state.queryParams.from ?? null,
		cancellationSessionId: (state): string | null => state.queryParams.cancellation_session_id ?? null,
		// The `?from` on THIS page load only (null on reloads, the Auth0 return, and in-app nav).
		// main.ts ANDs it with the funnel-entry path allowlist (see arrivalPathname) to emit
		// CancellationSessionStart exactly once, at the genuine entry.
		arrivalFrom: (): string | null => arrivalQuery.get('from'),
		// The pathname THIS page load arrived at, captured before the router's first redirect.
		arrivalPathname: (): string => arrivalPath,
		// Environment flag: this browsing session was opened from the mobile app's webview (the app
		// appends ?from=app to every link it opens). Deliberately rides the analytics session
		// snapshot — same lscache persistence, 24 h TTL fixed at establishment. The accepted
		// trade-offs (Android Custom Tab storage sharing, the TTL cliff) live in ADR 0015; read it
		// before "fixing" either here.
		isWebview: (state): boolean => state.queryParams.from === 'app',
	},

	actions: {
		/**
		 * Establish the analytics session id. MUST be called exactly once, from main.ts at bootstrap —
		 * never from a router guard, a route watcher, or the `/` component. A full page load is the only
		 * thing that may (re)generate the id; in-app back/forward is client-side and never re-runs this,
		 * which is what keeps `/?from=landing → /signin → Back → /?from=landing` from changing sessionId.
		 */
		init() {
			const stored = readStored()
			const cancellationId = arrivalQuery.get('cancellation_session_id') || null
			const from = arrivalQuery.get('from') || null

			// Reuse only when this load carries neither a cancellation id nor ?from, and a fresh
			// (<24h) id is stored. This is the sole branch that does NOT write, so the original 24h
			// expiry stays fixed rather than sliding on every re-entry.
			if (!cancellationId && !from && stored) {
				this.sessionId = stored.sessionId
				this.queryParams = stored.queryParams
				return
			}

			// Establish a new session: a cancellation id (from the mobile app) wins; otherwise a uuid.
			this.sessionId = cancellationId ?? uuid()
			this.queryParams = snapshot(arrivalQuery)
			writeStored({ sessionId: this.sessionId, queryParams: this.queryParams })
		},
	},
})
